import { IonModal } from "@ionic/react";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useToast from "../../hooks/useToast";
import { SuiviMemberDetail, SuiviSequenceBase } from "../../services/suivi.service";
import dayjs from "dayjs";
import { DueChip, ListCard, QuietTag, Row, RowMain, RowSub, RowTitle, StatusText } from "./suiviUi";

type Props = {
  member: SuiviMemberDetail | null;
  /** Ordinal-aware role label (« Enfant 2 ») computed by the group space. */
  roleLabel?: string;
  /** The member's own pending questionnaires — the call script. */
  pending: SuiviSequenceBase[];
  onClose: () => void;
  /** POSTs the server-side relance stamp (shared with site colleagues). */
  onMarkReminded: () => Promise<void>;
};

/**
 * Fiche d'appel — the phone call as a unit of work: the number front and center
 * (tel: on mobile, selectable text + copy for locked hospital desktops), the
 * member's pending questionnaires as the call script, and the shared
 * "relancé" stamp. Bottom sheet chrome comes from theme/modal.scss
 * (.resonance-sheetmodal) — no ModalCard in sheets.
 */
const CallSheet: FC<Props> = ({ member, roleLabel, pending, onClose, onMarkReminded }) => {
  const { t } = useTranslation();
  const present = useToast();
  const [stamping, setStamping] = useState(false);

  const stampedToday = member?.last_reminder_stamp
    ? dayjs(member.last_reminder_stamp.at).isSame(dayjs(), "day")
    : false;

  const copyNumber = async (): Promise<void> => {
    if (!member?.phone_number) return;
    try {
      await navigator.clipboard.writeText(member.phone_number);
      present(t("suivi_copied"));
    } catch {
      // Clipboard can be blocked on locked-down browsers; the number stays
      // selectable text as the fallback.
    }
  };

  const copyEmail = async (): Promise<void> => {
    if (!member?.email) return;
    try {
      await navigator.clipboard.writeText(member.email);
      present(t("suivi_email_copied"));
    } catch {
      // Same fallback as the number: the address stays selectable text.
    }
  };

  const markReminded = async (): Promise<void> => {
    setStamping(true);
    try {
      await onMarkReminded();
    } catch {
      present(t("common_server_error_title"));
    } finally {
      setStamping(false);
    }
  };

  const sheetRole = roleLabel ?? member?.role_label ?? null;
  const name = member
    ? [member.firstname, member.lastname].filter(Boolean).join(" ") ||
      sheetRole ||
      t("suivi_member_generic")
    : "";

  return (
    <IonModal
      className="resonance-sheetmodal"
      isOpen={member !== null}
      backdropDismiss={true}
      // onDidDismiss (not onWillDismiss): nulling the member at animation START
      // would blank the sheet content while it slides down.
      onDidDismiss={onClose}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      canDismiss={true}
    >
      {member && (
        <Sheet>
          <Name>{name}</Name>
          {/* Role line only when it adds information (unnamed member: the title
              already IS the ordinal role label — no duplicate line). */}
          {sheetRole && sheetRole !== name && <Role>{sheetRole}</Role>}

          {member.phone_number && (
            <>
              <PhoneNumber>{member.phone_number}</PhoneNumber>
              <CallLink href={`tel:${member.phone_number}`}>
                {t("suivi_call")}
              </CallLink>
              <CopyButton type="button" onClick={copyNumber}>
                {t("suivi_copy_number")}
              </CopyButton>
            </>
          )}

          {/* Email — same role-config gating as the phone; mailto on tap,
              selectable + copy for locked-down desktops. */}
          {member.email && (
            <>
              <EmailLink href={`mailto:${member.email}`}>{member.email}</EmailLink>
              <CopyButton type="button" onClick={copyEmail}>
                {t("suivi_copy_email")}
              </CopyButton>
            </>
          )}

          {pending.length > 0 && (
            <>
              <SheetSection>{t("suivi_during_call")}</SheetSection>
              <ListCard>
                {pending.map((row) => (
                  <Row key={row.user_sequence_id}>
                    <RowMain>
                      <RowTitle style={{ fontSize: 14 }}>
                        {row.label ?? t("suivi_questionnaire")}
                      </RowTitle>
                      <RowSub>
                        <StatusText $active={row.status === "IN_PROGRESS"}>
                          {row.status === "IN_PROGRESS"
                            ? t("suivi_status_in_progress", {
                                answered: row.progress.answered,
                                total: row.progress.total,
                              })
                            : t("suivi_status_not_started")}
                        </StatusText>
                        <DueChip endDate={row.end_date} t={t} />
                        {row.avg_time_to_answer != null && row.avg_time_to_answer > 0 && (
                          <span>{t("suivi_minutes", { count: row.avg_time_to_answer })}</span>
                        )}
                      </RowSub>
                    </RowMain>
                  </Row>
                ))}
              </ListCard>
            </>
          )}

          <StampArea>
            {stampedToday ? (
              <QuietTag>
                {t("suivi_reminded_on", {
                  date: dayjs(member.last_reminder_stamp!.at).format("DD/MM"),
                })}
              </QuietTag>
            ) : (
              <StampButton type="button" disabled={stamping} onClick={markReminded}>
                {t("suivi_mark_reminded")}
              </StampButton>
            )}
          </StampArea>
        </Sheet>
      )}
    </IonModal>
  );
};

const Sheet = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  background: var(--app-color-background, #ffffff);
  padding: 18px 20px 24px;
`;

const Name = styled.h2`
  margin: 6px 0 0;
  font-size: 17px;
  font-weight: 700;
  text-align: center;
  color: var(--app-color-text, #0f172a);
`;

const Role = styled.p`
  margin: 2px 0 14px;
  font-size: 13px;
  text-align: center;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);
`;

/* Selectable on purpose — hospital desktops without tel: support. */
const PhoneNumber = styled.p`
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
  text-align: center;
  user-select: all;
  color: var(--app-color-text, #0f172a);
`;

const CallLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  border-radius: 999px;
  background: var(--app-color-primary, #2563eb);
  color: var(--app-color-primary-contrast, #ffffff);
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.85;
  }
`;

/* Selectable + tappable: mailto on tap, text-select for copy on desktops. */
const EmailLink = styled.a`
  display: block;
  margin: 14px 0 0;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  word-break: break-all;
  user-select: all;
  color: var(--app-color-primary, #2563eb);
  text-decoration: none;
`;

const CopyButton = styled.button`
  margin: 10px auto 0;
  border: none;
  border-radius: 999px;
  min-height: 38px;
  padding: 0 18px;
  font-size: 13.5px;
  font-weight: 600;
  background: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.05);
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.75);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

const SheetSection = styled.p`
  margin: 18px 2px 8px;
  font-size: 11.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.55);
`;

const StampArea = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const StampButton = styled.button`
  width: 100%;
  border: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.12);
  border-radius: 14px;
  min-height: 48px;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  color: var(--app-color-text, #0f172a);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    opacity: 0.5;
  }
`;

export default CallSheet;
